export const physiologyModule = {
  id: "physiology",
  title: "Thyroid Physiology",
  sections: [
    // ── HPT Axis ────────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>The Hypothalamic-Pituitary-Thyroid (HPT) Axis</h3>
      <p>Thyroid hormone production is controlled by a feedback loop involving three levels:</p>
      <p>1. The <strong>hypothalamus</strong> releases <b class="purple-term">thyrotropin-releasing hormone (TRH)</b> into the portal circulation.</p>
      <p>2. TRH acts on the <strong>anterior pituitary</strong>, stimulating it to release <b class="purple-term">thyroid-stimulating hormone (TSH)</b> into the bloodstream.</p>
      <p>3. TSH acts on the <strong>thyroid gland</strong>, stimulating it to produce and release <b class="purple-term">T3 (triiodothyronine)</b> and <b class="purple-term">T4 (thyroxine)</b>.</p>
      <p>T3 and T4 then exert <strong>negative feedback</strong> on both the hypothalamus (inhibiting TRH) and the anterior pituitary (inhibiting TSH release). This keeps thyroid hormone levels within a narrow range.</p>
      <p>Understanding this axis is essential for interpreting thyroid blood tests:</p>
      <div class="highlight-box">Interpreting TSH levels:
        <ul>
          <li><strong>Primary hypothyroidism</strong>: low T4 → high TSH (pituitary compensates)</li>
          <li><strong>Primary hyperthyroidism</strong>: high T4/T3 → low TSH (negative feedback)</li>
          <li><strong>Secondary (central) hypothyroidism</strong>: low T4 AND low TSH (pituitary failure)</li>
          <li><strong>Subclinical hypothyroidism</strong>: normal T4, high TSH (early/compensated)</li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "Which hormone does the hypothalamus release to stimulate the pituitary in the HPT axis?",
      options: [
        "Thyroid-stimulating hormone (TSH)",
        "Thyrotropin-releasing hormone (TRH)",
        "Thyroxine (T4)",
        "Corticotropin-releasing hormone (CRH)",
      ],
      correct: 1,
      explanation:
        "The hypothalamus releases TRH (thyrotropin-releasing hormone) into the portal circulation, which stimulates the anterior pituitary to release TSH.",
    },
    {
      type: "question",
      text: "A patient has low T4 and high TSH. What does this indicate?",
      options: [
        "Primary hyperthyroidism",
        "Secondary hypothyroidism (pituitary failure)",
        "Primary hypothyroidism",
        "Subclinical hyperthyroidism",
      ],
      correct: 2,
      explanation:
        "Low T4 with high TSH indicates primary hypothyroidism. The thyroid is underproducing T4, so the pituitary responds by increasing TSH to try to stimulate the gland. The high TSH confirms the problem is in the thyroid, not the pituitary.",
    },
    {
      type: "question",
      text: "A patient has low T4 and low TSH. What is the most likely cause?",
      options: [
        "Graves' disease",
        "Primary hypothyroidism",
        "Secondary (central) hypothyroidism due to pituitary or hypothalamic failure",
        "Excess iodine intake",
      ],
      correct: 2,
      explanation:
        "Low T4 with low (or inappropriately normal) TSH suggests secondary or central hypothyroidism — the pituitary is not secreting enough TSH to stimulate the thyroid. This occurs with pituitary tumors, Sheehan's syndrome, or hypothalamic disease.",
    },
    {
      type: "question",
      text: "How do T3 and T4 regulate their own production?",
      options: [
        "They stimulate TRH and TSH release (positive feedback)",
        "They inhibit TRH from the hypothalamus and TSH from the pituitary (negative feedback)",
        "They directly inhibit iodine uptake by the thyroid",
        "They have no direct effect on the pituitary",
      ],
      correct: 1,
      explanation:
        "T3 and T4 exert negative feedback on both the hypothalamus (inhibiting TRH) and the anterior pituitary (inhibiting TSH release). This keeps thyroid hormone levels within a narrow physiological range.",
    },

    // ── Hormone synthesis ───────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Thyroid Hormone Synthesis</h3>
      <p>Thyroid hormone synthesis requires <strong>iodine</strong> and takes place in several steps:</p>
      <p><strong>Step 1 — Iodide uptake:</strong> Iodide (I⁻) from the diet enters the blood. Follicular cells actively transport I⁻ from the blood into the cell via the <b class="purple-term">sodium-iodide symporter (NIS)</b> on the basolateral membrane. NIS co-transports 2 Na⁺ with 1 I⁻, using the Na⁺ gradient maintained by the Na⁺/K⁺-ATPase (secondary active transport). Pendrin then moves I⁻ from the cell into the follicle lumen.</p>
      <p><strong>Step 2 — Oxidation:</strong> <b class="purple-term">Thyroid peroxidase (TPO)</b> oxidises I⁻ to I₂ (using hydrogen peroxide), making it reactive.</p>
      <p><strong>Step 3 — Organification:</strong> TPO attaches iodine to tyrosine residues on <b class="purple-term">thyroglobulin</b> (a glycoprotein produced by follicular cells and secreted into the colloid):
      <ul>
        <li>Tyrosine + 1 iodine → <strong>MIT</strong> (monoiodotyrosine)</li>
        <li>Tyrosine + 2 iodines → <strong>DIT</strong> (diiodotyrosine)</li>
      </ul></p>
      <p><strong>Step 4 — Coupling:</strong> TPO couples iodotyrosine residues on thyroglobulin:
      <ul>
        <li>DIT + DIT → <b class="purple-term">T4</b> (thyroxine, tetraiodothyronine) — the main product</li>
        <li>MIT + DIT → <b class="purple-term">T3</b> (triiodothyronine) — the active form</li>
      </ul></p>
      <p><strong>Step 5 — Storage:</strong> Iodinated thyroglobulin is stored in the colloid.</p>
      <p><strong>Step 6 — Release:</strong> TSH stimulates follicular cells to endocytose colloid. Lysosomes cleave thyroglobulin, releasing T3 and T4 into the bloodstream. MIT and DIT are recycled — their iodine is recovered by <b class="purple-term">deiodinase</b>.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Synthesis mnemonic: "I OC SR"</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">I</b>odide uptake via NIS</li>
            <li><b class="purple-term">O</b>xidation by TPO (I⁻ → I₂)</li>
            <li><b class="purple-term">C</b>oupling by TPO (MIT + DIT → T3; DIT + DIT → T4)</li>
            <li><b class="purple-term">S</b>torage in colloid as iodinated thyroglobulin</li>
            <li><b class="purple-term">R</b>elease: endocytosis → lysosomal cleavage → T3 and T4 secreted</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "Which transporter moves iodide from the blood into follicular cells?",
      options: [
        "Pendrin",
        "Na⁺/K⁺-ATPase",
        "Sodium-iodide symporter (NIS)",
        "Thyroid peroxidase (TPO)",
      ],
      correct: 2,
      explanation:
        "The sodium-iodide symporter (NIS) on the basolateral membrane of follicular cells actively transports iodide (I⁻) from the blood into the cell, co-transporting 2 Na⁺ per I⁻ (secondary active transport). Pendrin then moves I⁻ from the cell into the follicle lumen.",
    },
    {
      type: "question",
      text: "Which enzyme is responsible for both oxidising iodide and iodinating thyroglobulin (organification)?",
      options: [
        "Sodium-iodide symporter (NIS)",
        "Thyroid peroxidase (TPO)",
        "Deiodinase",
        "Pendrin",
      ],
      correct: 1,
      explanation:
        "Thyroid peroxidase (TPO) performs both the oxidation of I⁻ to I₂ and the organification step (attaching iodine to tyrosine residues on thyroglobulin). It also catalyses the coupling of iodotyrosines to form T3 and T4. This is why TPO inhibitors (PTU, methimazole) are effective antithyroid drugs.",
    },
    {
      type: "question",
      text: "What are the two iodotyrosine building blocks that combine to form T4?",
      options: [
        "MIT + MIT",
        "MIT + DIT",
        "DIT + DIT",
        "MIT + tyrosine",
      ],
      correct: 2,
      explanation:
        "T4 (thyroxine, tetraiodothyronine) is formed by coupling two DIT (diiodotyrosine) residues. T3 (triiodothyronine) is formed by coupling one MIT with one DIT. T4 is the predominant product of the thyroid gland.",
    },
    {
      type: "question",
      text: "What happens to MIT and DIT after thyroglobulin is cleaved and T3/T4 are released?",
      options: [
        "They are secreted into the bloodstream along with T3 and T4",
        "Their iodine is recovered by deiodinase and recycled within the follicular cell",
        "They are stored in the colloid for later use",
        "They are converted to reverse T3 (rT3)",
      ],
      correct: 1,
      explanation:
        "After lysosomal cleavage of thyroglobulin, MIT and DIT are deiodinated within the follicular cell by deiodinase. This recycling of iodine is efficient and essential, since the body's iodine stores are limited.",
    },

    // ── Wolff-Chaikoff effect ───────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>The Wolff-Chaikoff Effect</h3>
      <p>When the body is exposed to a <strong>large dose of iodine</strong>, the thyroid transiently <strong>stops producing thyroid hormone</strong>. This is called the <b class="purple-term">Wolff-Chaikoff effect</b>.</p>
      <p>High intracellular iodine inhibits the organification step (iodination of thyroglobulin), effectively shutting down hormone synthesis for 1–2 weeks. The thyroid then <strong>"escapes"</strong> from this inhibition by downregulating NIS, reducing iodine uptake back to normal levels.</p>
      <p>The Wolff-Chaikoff effect has two important clinical applications:</p>
      <p>1. <strong>Before thyroid surgery</strong>: Lugol's iodine (potassium iodide solution) is given for 10–14 days pre-operatively. The high iodine load reduces thyroid vascularity and hormone secretion, making surgery safer.</p>
      <p>2. <strong>After nuclear accidents</strong>: Potassium iodide (KI) tablets are distributed to saturate the thyroid with stable iodine, preventing it from taking up radioactive iodine-131 from fallout.</p>
      <div class="highlight-box">Note — the <strong>Jod-Basedow effect</strong> is the opposite:
        <ul>
          <li>In patients with autonomous thyroid tissue (e.g. toxic nodular goiter), excess iodine <em>triggers</em> hyperthyroidism</li>
          <li>The autonomous tissue cannot downregulate NIS, so it uses the iodine load to overproduce hormone</li>
          <li>Risk with iodine-containing contrast dyes and amiodarone</li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What is the Wolff-Chaikoff effect?",
      options: [
        "Iodine deficiency causing goiter",
        "High iodine load transiently inhibiting thyroid hormone synthesis",
        "Excess iodine triggering hyperthyroidism in autonomous thyroid tissue",
        "TSH stimulating iodide uptake via NIS",
      ],
      correct: 1,
      explanation:
        "The Wolff-Chaikoff effect is the transient inhibition of thyroid hormone synthesis caused by a large iodine load. High intracellular iodine inhibits organification. The thyroid escapes this inhibition after 1–2 weeks by downregulating NIS.",
    },
    {
      type: "question",
      text: "Why is Lugol's iodine given before thyroid surgery?",
      options: [
        "To increase T3 and T4 levels before surgery",
        "To exploit the Wolff-Chaikoff effect — reducing gland vascularity and hormone secretion",
        "To prevent post-operative hypocalcemia",
        "To block the sodium-iodide symporter permanently",
      ],
      correct: 1,
      explanation:
        "Lugol's iodine (potassium iodide) exploits the Wolff-Chaikoff effect: the high iodine load transiently reduces thyroid vascularity and inhibits hormone secretion for 10–14 days. This makes the gland firmer and less vascular, reducing intraoperative bleeding.",
    },

    // ── T3 vs T4 ────────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>T3 vs. T4</h3>
      <p>The thyroid produces <strong>far more T4 than T3</strong> — approximately 80% of thyroid secretion is T4. However, T3 is the biologically active form at the cellular level.</p>
      <p>Most circulating T3 (~80%) does not come directly from the thyroid — it is produced in <strong>peripheral tissues</strong> (liver, kidney, muscle) by <b class="purple-term">deiodinase type 1</b>, which removes one iodine from T4 to convert it to T3. This makes T4 essentially a <strong>"prohormone"</strong>.</p>
      <p>T3 acts by entering cells and binding to <strong>nuclear thyroid hormone receptors</strong>, where it directly regulates gene transcription.</p>
      <div class="highlight-box">Key differences between T3 and T4:
        <ul>
          <li><strong>Potency</strong>: T3 is 3–4× more potent than T4</li>
          <li><strong>Half-life</strong>: T4 ~7 days; T3 ~1 day (T4 lasts longer)</li>
          <li><strong>Thyroid production</strong>: T4 predominates (80% of secretion)</li>
          <li><strong>Source of T3</strong>: ~80% comes from peripheral conversion of T4</li>
          <li><strong>Active form</strong>: T3 (acts directly on nuclear receptors)</li>
        </ul>
      </div>
      <p>There is also <b class="purple-term">reverse T3 (rT3)</b>, an inactive metabolite formed when T4 is converted by deiodinase type 3 instead of type 1. rT3 has no hormonal activity. Its production increases during illness, fasting, and physiological stress.</p>`,
    },
    {
      type: "question",
      text: "Which is the biologically active form of thyroid hormone at the cellular level?",
      options: [
        "T4 (thyroxine)",
        "T3 (triiodothyronine)",
        "Reverse T3 (rT3)",
        "TSH",
      ],
      correct: 1,
      explanation:
        "T3 is the active form. It enters cells and binds directly to nuclear thyroid hormone receptors, regulating gene transcription. T4 acts primarily as a prohormone that is converted to T3 in peripheral tissues.",
    },
    {
      type: "question",
      text: "Where is most circulating T3 produced?",
      options: [
        "Directly by the thyroid gland follicular cells",
        "By peripheral tissues (liver, kidney, muscle) via conversion of T4 by deiodinase",
        "By the anterior pituitary",
        "By parafollicular C cells",
      ],
      correct: 1,
      explanation:
        "Approximately 80% of circulating T3 is produced in peripheral tissues (mainly liver, kidney, and muscle) by deiodinase type 1, which removes one iodine atom from T4. Only ~20% of T3 comes directly from the thyroid gland.",
    },
    {
      type: "question",
      text: "T4 has a much longer half-life than T3. Why is this clinically significant?",
      options: [
        "T4 is more potent, so it needs to last longer",
        "T4 acts as a stable reservoir that provides a steady supply of T3 via peripheral conversion",
        "T4 is recycled more efficiently by deiodinase",
        "T4 has higher protein binding, making it active for longer",
      ],
      correct: 1,
      explanation:
        "T4's long half-life (~7 days vs. T3's ~1 day) means it acts as a stable hormonal reservoir. Peripheral tissues continuously convert T4 to T3 as needed, providing a steady supply of active hormone. This is also why levothyroxine (T4 replacement) is dosed once daily.",
    },

    // ── Protein binding ─────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Protein Binding of Thyroid Hormones</h3>
      <p>Most T3 and T4 in the blood are <strong>bound to carrier proteins</strong> and are biologically <strong>inactive</strong>. Only the small <strong>free (unbound) fraction</strong> is active and can enter cells.</p>
      <p>The main carrier proteins are:</p>
      <ul>
        <li><b class="purple-term">Thyroxine-binding globulin (TBG)</b>: carries ~70% of T4 and T3; the major carrier</li>
        <li><b class="purple-term">Transthyretin (TTR)</b>: carries ~10–15%</li>
        <li><b class="purple-term">Albumin</b>: carries ~15–20% (low affinity but high capacity)</li>
      </ul>
      <p>Because only the free hormone is active, changes in carrier protein levels alter <strong>total</strong> T4/T3 measurements but not the free hormone level or the patient's symptoms:</p>
      <div class="highlight-box">Causes of altered TBG levels:
        <ul>
          <li><strong>Increased TBG</strong> (↑ total T4, normal free T4): pregnancy, oestrogen/OCP use, liver disease (acute hepatitis)</li>
          <li><strong>Decreased TBG</strong> (↓ total T4, normal free T4): nephrotic syndrome (protein loss in urine), androgens, glucocorticoids, liver failure (chronic)</li>
        </ul>
      </div>
      <p>This is why <strong>free T4 (fT4)</strong> is measured in clinical practice — it reflects actual thyroid function regardless of carrier protein levels.</p>`,
    },
    {
      type: "question",
      text: "Which fraction of circulating T4 is biologically active?",
      options: [
        "The TBG-bound fraction",
        "The transthyretin-bound fraction",
        "The free (unbound) fraction",
        "The albumin-bound fraction",
      ],
      correct: 2,
      explanation:
        "Only the free (unbound) fraction of T4 and T3 is biologically active and can enter cells. The majority of circulating T4 is bound to carrier proteins (TBG, transthyretin, albumin), which act as a reservoir and buffer.",
    },
    {
      type: "question",
      text: "A pregnant woman has a high total T4 but normal free T4 and no symptoms of hyperthyroidism. What explains this?",
      options: [
        "Pregnancy causes the thyroid to overproduce T4",
        "Oestrogen increases TBG levels, raising total T4 while free T4 remains normal",
        "The placenta converts excess T4 to rT3",
        "Pregnancy increases peripheral T4-to-T3 conversion",
      ],
      correct: 1,
      explanation:
        "Oestrogen (from pregnancy or the OCP) increases hepatic production of TBG. More carrier protein means more T4 is bound, raising the total T4. However, free T4 remains normal, so there are no symptoms of hyperthyroidism. This is why free T4 is used in clinical practice.",
    },
    {
      type: "question",
      text: "Which carrier protein transports the majority (~70%) of circulating T4 and T3?",
      options: [
        "Albumin",
        "Transthyretin (TTR)",
        "Thyroxine-binding globulin (TBG)",
        "Transferrin",
      ],
      correct: 2,
      explanation:
        "Thyroxine-binding globulin (TBG) is the major carrier of thyroid hormones in the blood, transporting approximately 70% of T4 and T3. It has a high affinity but low capacity for thyroid hormones.",
    },

    // ── Metabolic effects ───────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Metabolic Effects of Thyroid Hormones</h3>
      <p>Thyroid hormones (mainly T3) regulate virtually every tissue in the body. Their main effects include:</p>
      <p><strong>Metabolism:</strong> T3 increases the <b class="purple-term">basal metabolic rate (BMR)</b> — the rate of oxygen consumption and heat production at rest. This calorigenic effect is why hyperthyroidism causes heat intolerance and hypothyroidism causes cold intolerance.</p>
      <p><strong>Cardiovascular:</strong> T3 increases <strong>heart rate</strong> and <strong>cardiac output</strong>. It also upregulates β-adrenergic receptors, amplifying the effect of catecholamines (adrenaline, noradrenaline) on the heart and vasculature.</p>
      <p><strong>Metabolism of macronutrients:</strong> T3 increases glucose absorption from the gut, glycogenolysis, and gluconeogenesis. It also increases lipolysis (fat breakdown).</p>
      <p><strong>Growth and development:</strong> Thyroid hormones are essential for normal <strong>brain development</strong> (especially in the fetus and neonate) and bone growth. Deficiency in early life causes <b class="purple-term">cretinism</b> — severe intellectual disability, stunted growth, and deafness.</p>
      <p><strong>Sympathomimetic effect:</strong> T3 increases the number and sensitivity of β-adrenergic receptors, explaining why hyperthyroid patients have symptoms resembling sympathetic overactivation (palpitations, tremor, anxiety, sweating).</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Effects of thyroid hormones: "METABOLISM"</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">M</b>etabolic rate ↑ (BMR, O₂ consumption, heat)</li>
            <li><b class="purple-term">E</b>xcitability ↑ (β-receptors upregulated → HR, CO)</li>
            <li><b class="purple-term">T</b>emperature ↑ (calorigenic)</li>
            <li><b class="purple-term">A</b>nabolic at normal levels (protein synthesis); catabolic in excess</li>
            <li><b class="purple-term">B</b>rain and bone development (essential in fetus/neonate)</li>
            <li><b class="purple-term">O</b>xidative phosphorylation ↑</li>
            <li><b class="purple-term">L</b>ipolysis ↑ and glucose absorption ↑</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "Why do patients with hyperthyroidism experience palpitations and tremors?",
      options: [
        "Excess T3 directly stimulates the heart muscle to contract faster",
        "T3 upregulates β-adrenergic receptors, amplifying the effects of catecholamines",
        "Excess T4 blocks the sodium-potassium ATPase in cardiac muscle",
        "High TSH directly stimulates adrenergic receptors",
      ],
      correct: 1,
      explanation:
        "T3 upregulates β-adrenergic receptors, making tissues more sensitive to catecholamines (adrenaline, noradrenaline). In hyperthyroidism, this amplified sympathetic response causes palpitations, tremors, anxiety, and sweating. This is why β-blockers (e.g. propranolol) are used to control symptoms in hyperthyroidism.",
    },
    {
      type: "question",
      text: "What is the consequence of severe thyroid hormone deficiency in a newborn?",
      options: [
        "Graves' disease",
        "Cretinism — severe intellectual disability, stunted growth, and deafness",
        "Medullary thyroid carcinoma",
        "Hashimoto's thyroiditis",
      ],
      correct: 1,
      explanation:
        "Thyroid hormones are essential for normal brain development and myelination in fetal and neonatal life. Severe deficiency causes cretinism, characterised by profound intellectual disability, growth retardation, deafness, and coarse facial features. This is why newborn thyroid screening is universal.",
    },
    {
      type: "question",
      text: "How do thyroid hormones affect basal metabolic rate (BMR)?",
      options: [
        "They decrease BMR to conserve energy",
        "They have no direct effect on BMR",
        "They increase BMR by raising O₂ consumption and heat production",
        "They increase BMR only in the liver",
      ],
      correct: 2,
      explanation:
        "T3 increases the basal metabolic rate by raising O₂ consumption and heat production (calorigenic effect) in most tissues. This explains the heat intolerance and weight loss in hyperthyroidism, and the cold intolerance and weight gain in hypothyroidism.",
    },

    // ── Hypothyroidism ──────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Hypothyroidism</h3>
      <p><strong>Hypothyroidism</strong> is a state of deficient thyroid hormone. Because T3 affects nearly every organ system, the clinical features reflect a generalised slowing of metabolism:</p>
      <p><strong>Common causes:</strong></p>
      <ul>
        <li><b class="purple-term">Hashimoto's thyroiditis</b>: most common cause in developed countries; autoimmune destruction of the thyroid (anti-TPO and anti-thyroglobulin antibodies)</li>
        <li><b class="purple-term">Iodine deficiency</b>: most common cause worldwide</li>
        <li>Drugs: amiodarone, lithium, interferon-α</li>
        <li>Post-thyroidectomy or radioactive iodine treatment</li>
      </ul>
      <p><strong>Clinical features</strong> — think of everything slowing down:</p>
      <div class="highlight-box">Signs and symptoms of hypothyroidism:
        <ul>
          <li>Fatigue, lethargy, depression</li>
          <li><strong>Cold intolerance</strong> (decreased BMR)</li>
          <li><strong>Weight gain</strong></li>
          <li>Constipation (decreased gut motility)</li>
          <li><strong>Bradycardia</strong></li>
          <li>Dry skin, brittle hair, hair loss</li>
          <li><strong>Delayed relaxation of deep tendon reflexes</strong> (classic sign)</li>
          <li>Myxoedema: non-pitting oedema due to accumulation of glycosaminoglycans</li>
          <li>Goitre (in iodine deficiency or Hashimoto's — TSH is high, stimulating gland growth)</li>
        </ul>
      </div>
      <p><strong>Lab findings:</strong> low free T4, high TSH (in primary hypothyroidism).</p>
      <p><strong>Treatment:</strong> <b class="purple-term">levothyroxine</b> (synthetic T4), dosed once daily.</p>`,
    },
    {
      type: "question",
      text: "What is the most common cause of hypothyroidism in developed countries?",
      options: [
        "Iodine deficiency",
        "Hashimoto's thyroiditis",
        "Post-radioactive iodine treatment",
        "Lithium therapy",
      ],
      correct: 1,
      explanation:
        "Hashimoto's thyroiditis is the most common cause of hypothyroidism in developed countries. It is an autoimmune disease characterised by anti-TPO and anti-thyroglobulin antibodies that destroy follicular cells. Iodine deficiency is the most common cause worldwide.",
    },
    {
      type: "question",
      text: "Which of the following is a classic clinical sign of hypothyroidism?",
      options: [
        "Heat intolerance and weight loss",
        "Tachycardia and tremor",
        "Delayed relaxation of deep tendon reflexes and cold intolerance",
        "Exophthalmos and pretibial myxoedema",
      ],
      correct: 2,
      explanation:
        "Delayed relaxation of deep tendon reflexes and cold intolerance are classic signs of hypothyroidism. The delay in reflex relaxation is due to impaired muscle metabolism. Heat intolerance, tachycardia, tremor, exophthalmos, and pretibial myxoedema are features of hyperthyroidism (the latter two specifically in Graves' disease).",
    },
    {
      type: "question",
      text: "What is myxoedema?",
      options: [
        "Pitting oedema caused by heart failure in hypothyroidism",
        "Non-pitting oedema caused by accumulation of glycosaminoglycans in the skin and tissues",
        "Exophthalmos caused by periorbital fat deposition",
        "Goitre caused by TSH overstimulation",
      ],
      correct: 1,
      explanation:
        "Myxoedema is a non-pitting oedema caused by the accumulation of glycosaminoglycans (mucopolysaccharides) in the dermis and other tissues in severe hypothyroidism. It affects the face (causing a puffy, dull appearance), hands, and feet. It is distinct from pitting oedema caused by fluid retention.",
    },

    // ── Hyperthyroidism ─────────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Hyperthyroidism</h3>
      <p><strong>Hyperthyroidism</strong> is a state of excess thyroid hormone. Features reflect a generalised acceleration of metabolism and amplified sympathetic activity:</p>
      <p><strong>Common causes:</strong></p>
      <ul>
        <li><b class="purple-term">Graves' disease</b>: most common cause; TSH receptor antibodies (TRAb) constantly stimulate the thyroid (autoimmune)</li>
        <li>Toxic multinodular goitre</li>
        <li>Toxic adenoma (single autonomous nodule)</li>
        <li>Thyroiditis (transient hyperthyroidism from hormone leakage)</li>
        <li>Excess iodine (Jod-Basedow effect)</li>
      </ul>
      <p><strong>Clinical features</strong> — think of everything speeding up:</p>
      <div class="highlight-box">Signs and symptoms of hyperthyroidism:
        <ul>
          <li>Fatigue, anxiety, irritability, insomnia</li>
          <li><strong>Heat intolerance</strong>, sweating (increased BMR)</li>
          <li><strong>Weight loss</strong> despite increased appetite</li>
          <li>Diarrhoea (increased gut motility)</li>
          <li><strong>Tachycardia</strong>, palpitations, atrial fibrillation</li>
          <li>Tremor (fine tremor of outstretched hands)</li>
          <li>Warm, moist skin; hair loss</li>
          <li>Graves' disease only: <strong>exophthalmos</strong> (proptosis) and <strong>pretibial myxoedema</strong></li>
        </ul>
      </div>
      <p><strong>Lab findings:</strong> high free T4 and/or T3, low TSH (in primary hyperthyroidism).</p>
      <p><strong>Treatments:</strong> antithyroid drugs (PTU, methimazole), radioactive iodine (I-131), or thyroidectomy.</p>`,
    },
    {
      type: "question",
      text: "What is the most common cause of hyperthyroidism?",
      options: [
        "Toxic multinodular goitre",
        "Toxic adenoma",
        "Graves' disease",
        "Thyroiditis",
      ],
      correct: 2,
      explanation:
        "Graves' disease is the most common cause of hyperthyroidism. It is an autoimmune disease in which TSH receptor antibodies (TRAb) continuously stimulate the thyroid gland, leading to excess T3 and T4 production.",
    },
    {
      type: "question",
      text: "Which clinical features are unique to Graves' disease and not seen in other causes of hyperthyroidism?",
      options: [
        "Tachycardia and weight loss",
        "Exophthalmos and pretibial myxoedema",
        "Heat intolerance and tremor",
        "Atrial fibrillation and diarrhoea",
      ],
      correct: 1,
      explanation:
        "Exophthalmos (proptosis of the eyes) and pretibial myxoedema (skin thickening over the shins) are unique to Graves' disease. They are caused by the TSH receptor antibodies acting on orbital and skin fibroblasts, stimulating glycosaminoglycan deposition — they are not caused by excess thyroid hormone itself.",
    },
    {
      type: "question",
      text: "A patient with hyperthyroidism has a very low TSH and high free T4. Why is TSH low?",
      options: [
        "The pituitary is destroyed by high T4 levels",
        "High T3/T4 exerts negative feedback on the pituitary, suppressing TSH release",
        "TSH is converted to T3 in the bloodstream",
        "The TSH receptor is blocked by antithyroid drugs",
      ],
      correct: 1,
      explanation:
        "In primary hyperthyroidism, excess T3 and T4 suppress TSH via negative feedback on the anterior pituitary. A very low (suppressed) TSH with high free T4/T3 is the hallmark laboratory finding in primary hyperthyroidism.",
    },

    // ── Antithyroid drugs ───────────────────────────────────────────────────
    {
      type: "text",
      title: "Physiology",
      content: `<h3>Antithyroid Drugs</h3>
      <p>Several drugs are used to reduce thyroid hormone synthesis or manage its effects:</p>
      <p><b class="purple-term">Propylthiouracil (PTU)</b>: inhibits thyroid peroxidase (blocking organification and coupling) <strong>AND</strong> inhibits peripheral conversion of T4 → T3 (by blocking deiodinase type 1). Its dual action makes it the preferred drug in <strong>thyroid storm</strong> and in the <strong>first trimester of pregnancy</strong>.</p>
      <p><b class="purple-term">Methimazole</b>: inhibits thyroid peroxidase only (no effect on peripheral conversion). It is preferred in the <strong>second and third trimesters</strong> because PTU carries a risk of hepatotoxicity with prolonged use. Methimazole is teratogenic in the first trimester (risk of aplasia cutis).</p>
      <p><b class="purple-term">Radioactive iodine (I-131)</b>: taken up by the thyroid via NIS and destroys follicular cells through beta radiation. Used to ablate the thyroid in Graves' disease. Contraindicated in pregnancy.</p>
      <p><b class="purple-term">Propranolol (β-blocker)</b>: does not reduce T3/T4 levels, but blocks β-adrenergic receptors to rapidly control symptoms (heart rate, tremor, anxiety). Also inhibits peripheral T4 → T3 conversion at high doses.</p>
      <p><b class="purple-term">Amiodarone</b>: an antiarrhythmic drug containing ~37% iodine by weight. It can cause both hypothyroidism (Wolff-Chaikoff) and hyperthyroidism (Jod-Basedow). It also directly inhibits T4 → T3 conversion.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">PTU vs Methimazole</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">PTU</b>: inhibits TPO + peripheral conversion; use in <b>thyroid storm</b> and <b>1st trimester</b></li>
            <li><b class="purple-term">Methimazole</b>: inhibits TPO only; use in <b>2nd/3rd trimester</b> (avoid in 1st — aplasia cutis risk)</li>
          </ul>
        </div>
      </div>`,
    },
    {
      type: "question",
      text: "What is the key pharmacological difference between PTU and methimazole?",
      options: [
        "PTU blocks the sodium-iodide symporter; methimazole blocks thyroid peroxidase",
        "PTU inhibits TPO AND peripheral T4→T3 conversion; methimazole only inhibits TPO",
        "Methimazole inhibits TPO AND peripheral T4→T3 conversion; PTU only inhibits TPO",
        "PTU blocks TSH receptors; methimazole blocks thyroid peroxidase",
      ],
      correct: 1,
      explanation:
        "PTU inhibits both thyroid peroxidase (blocking organification) and peripheral conversion of T4 to T3 via deiodinase type 1. Methimazole inhibits TPO only. PTU's dual action makes it preferred in thyroid storm and the first trimester of pregnancy.",
    },
    {
      type: "question",
      text: "Why is PTU preferred over methimazole in the first trimester of pregnancy?",
      options: [
        "PTU is safer for the fetus because it does not cross the placenta",
        "Methimazole is associated with aplasia cutis (absent skin patch) in the fetus in the first trimester",
        "PTU has a faster onset of action in pregnancy",
        "Methimazole increases the risk of preeclampsia",
      ],
      correct: 1,
      explanation:
        "Methimazole use in the first trimester is associated with aplasia cutis (a congenital skin defect) and other teratogenic effects. PTU is therefore preferred in the first trimester. However, PTU carries a risk of hepatotoxicity with prolonged use, so methimazole is switched back in the second and third trimesters.",
    },
    {
      type: "question",
      text: "How does radioactive iodine (I-131) destroy the thyroid gland?",
      options: [
        "It blocks thyroid peroxidase permanently",
        "It is taken up by the thyroid via NIS and emits beta radiation that destroys follicular cells",
        "It competitively inhibits TSH binding to its receptor",
        "It cross-links thyroglobulin, preventing hormone synthesis",
      ],
      correct: 1,
      explanation:
        "I-131 is actively taken up by thyroid follicular cells via the sodium-iodide symporter (NIS), just like stable iodine. Once inside, it emits beta radiation that destroys the surrounding follicular cells. It is used to ablate the thyroid in Graves' disease but is contraindicated in pregnancy.",
    },
    {
      type: "question",
      text: "Which drug is used to rapidly control the symptoms of hyperthyroidism (tachycardia, tremor) without directly reducing T3/T4 levels?",
      options: [
        "Methimazole",
        "PTU",
        "Propranolol",
        "Levothyroxine",
      ],
      correct: 2,
      explanation:
        "Propranolol is a non-selective β-blocker that rapidly controls the adrenergic symptoms of hyperthyroidism (tachycardia, palpitations, tremor, anxiety) by blocking β-adrenergic receptors. It does not reduce thyroid hormone synthesis. At high doses it also inhibits peripheral T4→T3 conversion.",
    },
  ],
};
